import React, { useState, useEffect } from "react";
import { tendersAPI, aiAPI } from "../services/api";
import { GitCompare, X, Plus, Zap, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";

export default function Compare() {
  const [allTenders, setAllTenders] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [aiComparison, setAiComparison] = useState("");
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    tendersAPI.list({ page_size: 50, status: "open" }).then(res => setAllTenders(res.data.items));
  }, []);

  const addTender = (tender) => {
    if (selected.length >= 3) { toast.error("Maximum 3 tenders"); return; }
    if (selected.find(t => t.id === tender.id)) { toast.error("Already added"); return; }
    setSelected(prev => [...prev, tender]);
  };

  const removeTender = (id) => setSelected(prev => prev.filter(t => t.id !== id));

  const filtered = allTenders.filter(t =>
    !selected.find(s => s.id === t.id) &&
    (t.title.toLowerCase().includes(search.toLowerCase()) || t.agency.toLowerCase().includes(search.toLowerCase()))
  );

  const compareWithAI = async () => {
    if (selected.length < 2) { toast.error("Select at least 2 tenders"); return; }
    setComparing(true);
    setAiComparison("");
    try {
      const response = await aiAPI.compare({ tender_ids: selected.map(t => t.id) });
      setAiComparison(response.data.comparison || "");
      toast.success("Comparison complete!");
    } catch { toast.error("Comparison failed"); }
    finally { setComparing(false); }
  };

  const fields = [
    { label: "Contract Value", key: "contract_value_formatted", color: "var(--accent-green)" },
    { label: "Days Remaining", key: "days_remaining", suffix: " days" },
    { label: "Category", key: "category" },
    { label: "State", key: "state" },
    { label: "Agency", key: "agency" },
    { label: "Urgency", key: "urgency" },
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "28px" }}>
        <div style={{ width: "40px", height: "40px", background: "rgba(165,94,234,0.1)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <GitCompare size={20} color="#a55eea" />
        </div>
        <div>
          <h1 style={{ fontSize: "26px" }}>Tender Comparison Tool</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Compare up to 3 tenders side by side with AI strategy analysis</p>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: "24px" }}>
        <div className="card">
          <h3 style={{ fontSize: "14px", marginBottom: "12px", color: "var(--text-secondary)" }}>ADD TENDERS ({selected.length}/3)</h3>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tenders..." style={{ marginBottom: "12px" }} />
          <div style={{ maxHeight: "500px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
            {filtered.slice(0, 20).map(tender => (
              <button key={tender.id} onClick={() => addTender(tender)} style={{ padding: "10px", borderRadius: "8px", border: "1px solid var(--border)", background: "var(--bg-secondary)", cursor: "pointer", textAlign: "left", transition: "all 0.15s", display: "flex", alignItems: "flex-start", gap: "8px" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "var(--accent-green)"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>
                <Plus size={14} color="var(--accent-green)" style={{ flexShrink: 0, marginTop: "2px" }} />
                <div>
                  <div style={{ fontSize: "12px", fontWeight: "600", marginBottom: "2px", lineHeight: "1.3" }}>{tender.title.substring(0, 55)}...</div>
                  <div style={{ fontSize: "11px", color: "var(--accent-green)" }}>{tender.contract_value_formatted}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div>
          {selected.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: "80px" }}>
              <GitCompare size={48} color="var(--text-muted)" style={{ marginBottom: "16px", opacity: 0.3 }} />
              <h3 style={{ marginBottom: "8px" }}>Select tenders to compare</h3>
              <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Add 2-3 tenders from the left panel</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ display: "grid", gridTemplateColumns: `160px repeat(${selected.length}, 1fr)`, gap: "10px" }}>
                <div />
                {selected.map(tender => (
                  <div key={tender.id} className="card" style={{ padding: "14px", borderLeft: "3px solid var(--accent-green)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                      <span className={`badge badge-${tender.urgency}`} style={{ fontSize: "10px" }}>{tender.days_remaining}d</span>
                      <button onClick={() => removeTender(tender.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={13} /></button>
                    </div>
                    <div style={{ fontSize: "12px", fontWeight: "600", lineHeight: "1.4" }}>{tender.title.substring(0, 70)}...</div>
                  </div>
                ))}
              </div>
              {fields.map(field => (
                <div key={field.key} style={{ display: "grid", gridTemplateColumns: `160px repeat(${selected.length}, 1fr)`, gap: "10px", alignItems: "center" }}>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: "500" }}>{field.label}</div>
                  {selected.map(tender => (
                    <div key={tender.id} style={{ padding: "10px 14px", background: "var(--bg-card)", borderRadius: "8px", fontSize: "13px", fontWeight: field.color ? "700" : "500", color: field.color || "var(--text-primary)", border: "1px solid var(--border)" }}>
                      {tender[field.key] != null ? `${tender[field.key]}${field.suffix || ""}` : "N/A"}
                    </div>
                  ))}
                </div>
              ))}
              {selected.length >= 2 && (
                <button className="btn btn-primary" onClick={compareWithAI} disabled={comparing} style={{ width: "100%", justifyContent: "center", padding: "14px", background: "#a55eea" }}>
                  {comparing ? <><RefreshCw size={16} style={{ animation: "spin 1s linear infinite" }} /> Analyzing...</> : <><Zap size={16} /> AI Strategy Analysis</>}
                </button>
              )}
              {aiComparison && (
                <div className="card" style={{ borderColor: "#a55eea" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                    <Zap size={16} color="#a55eea" />
                    <span style={{ fontSize: "14px", fontWeight: "700", color: "#a55eea" }}>AI Strategy Analysis</span>
                  </div>
                  <pre style={{ fontFamily: "DM Sans, sans-serif", fontSize: "13px", lineHeight: "1.8", color: "var(--text-secondary)", whiteSpace: "pre-wrap" }}>{aiComparison}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
